import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import Panel from 'components/Panel';
import Break from 'components/Break';
import { BlueBorderButton, BlueButton } from 'components/Button';
import Text from 'components/Text';
import {
  SubPanel,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowDisplay,
  PanelRowTextForm,
  StyledForm,
  FullPanel,
  PanelContent,
  PanelButtonRow,
  RadioButtons,
  RadioOption,
  RadioInput,
  MultipleInputRowContainer,
  InputAndLabelContainer,
  LabledInput,
  InputUnitLabel
} from 'components/PanelComponents';
import SelectableSearchInput from 'components/SelectableSearchInput';
import { useDispatch, useSelector } from 'react-redux';
import { revertSidePanel, closeSidePanel } from 'reducers/sidePanel/actions';
import { openErrorModal } from 'reducers/modal/actions';
import { editDevice, deleteDevice } from 'reducers/device/actions';
import DeleteModal from 'components/Modals/DeleteModal';
import {
  convertTohhmmss,
  convertToSeconds,
  isExternallyManaged
} from 'utils/format';
import {
  LOCAL_RESOURCE_MAPPED_VALUES,
  PROTOCOL_OPTIONS
} from 'utils/appConstants';
import { ShowTooltipOnChildren } from 'components/Tooltip';

const deviceConfigFlag =
  window.env.REACT_APP_DEVICE_PROTOCOL_FLAG_ENABLED === 'true';
const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';

const EditDevice = ({ isActive }) => {
  const dispatch = useDispatch();
  const id = useSelector(state => state.sidePanel.id);
  const devices = useSelector(state => state.device.devices);
  const topoNodes = useSelector(state => state.topoNode.topoNodes);
  const indeNodes = useSelector(state => state.indeNode.indeNodes);
  const aggregators = useSelector(state => state.aggregator.aggregators);
  const [registrationPin, setRegistrationPin] = useState('');
  const [aggregatorId, setAggregatorId] = useState('');
  const [registrationPinErrorOccurred, setRegistrationPinErrorOccurred] =
    useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [device, setDevice] = useState({});
  const [deviceId, setDeviceId] = useState('');
  const [derInfoPostRateHours, setDerInfoPostRateHours] = useState(null);
  const [protocol, setProtocol] = useState(0);
  const [derInfoPostRateMinutes, setDerInfoPostRateMinutes] = useState(null);
  const [derInfoPostRateSeconds, setDerInfoPostRateSeconds] = useState(null);
  const [meterDataPostRateHours, setMeterDataPostRateHours] = useState(null);
  const [meterDataPostRateMinutes, setMeterDataPostRateMinutes] =
    useState(null);
  const [meterDataPostRateSeconds, setMeterDataPostRateSeconds] =
    useState(null);
  const [derInfoPostRateErrorOccurred, setDerInfoPostRateErrorOccurred] =
    useState(false);
  const [meterDataPostRateErrorOccurred, setMeterDataPostRateErrorOccurred] =
    useState(false);
  const [localTopologyNodeIdList, setLocalTopologyNodeIdList] = useState(null);
  const [remoteTopologyNodeIdList, setRemoteTopologyNodeIdList] =
    useState(null);
  const [localIndependentNodeIdList, setLocalIndependentNodeIdList] =
    useState(null);
  const [remoteIndependentNodeIdList, setRemoteIndependentNodeIdList] =
    useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const device = devices.find(device => device.deviceId === id) || {};
    const convertedDerInfoPostRate = Number.isInteger(device.edevPostRate)
      ? convertTohhmmss(device.edevPostRate)
      : {};
    const convertedMeterDataPostRate = Number.isInteger(device.mupPostRate)
      ? convertTohhmmss(device.mupPostRate)
      : {};

    // Separate out nodes into remote and local
    const sortRemoteAndLocalNodes = (
      nodeIdList,
      reduxStoreObject,
      nodeIdentifier
    ) => {
      const localNodes = [];
      const remoteNodes = [];
      nodeIdList.forEach(node => {
        const nodeInfo =
          reduxStoreObject.find(
            nodeItem => nodeItem[nodeIdentifier] === node
          ) || {};
        if (
          externallyManagedEdevs &&
          nodeInfo.localNode === LOCAL_RESOURCE_MAPPED_VALUES.REMOTE
        ) {
          remoteNodes.push(node);
        } else {
          localNodes.push(node);
        }
      });
      return { localNodes, remoteNodes };
    };
    const currentTopoNodes = device.topoNodeIdList || [];
    const currentIndeNodes = device.indeNodeIdList || [];
    const {
      localNodes: localTopologyNodeIdList,
      remoteNodes: remoteTopologyNodeIdList
    } = sortRemoteAndLocalNodes(currentTopoNodes, topoNodes, 'topoNodeId');
    const {
      localNodes: localIndependentNodeIdList,
      remoteNodes: remoteIndependentNodeIdList
    } = sortRemoteAndLocalNodes(currentIndeNodes, indeNodes, 'indeNodeId');
    setRegistrationPin(
      Number.isInteger(device.registrationPin) ? device.registrationPin : ''
    );
    setDerInfoPostRateHours(convertedDerInfoPostRate.hours);
    setDerInfoPostRateMinutes(convertedDerInfoPostRate.minutes);
    setDerInfoPostRateSeconds(convertedDerInfoPostRate.seconds);
    setMeterDataPostRateHours(convertedMeterDataPostRate.hours);
    setMeterDataPostRateMinutes(convertedMeterDataPostRate.minutes);
    setMeterDataPostRateSeconds(convertedMeterDataPostRate.seconds);
    setAggregatorId(device.aggregatorId || '');
    setProtocol(device.protocol || 0);
    setIsLoading(false);
    setDevice(device);
    setDeviceId(device.deviceId);
    setLocalTopologyNodeIdList(localTopologyNodeIdList);
    setRemoteIndependentNodeIdList(remoteIndependentNodeIdList);
    setRemoteTopologyNodeIdList(remoteTopologyNodeIdList);
    setLocalIndependentNodeIdList(localIndependentNodeIdList);
  }, []);

  const handleInputChange = event => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    if (name === 'deviceId') {
      setDeviceId(value);
    } else if (name === 'registrationPin') {
      setRegistrationPin(value);
    } else if (name === 'derInfoPostRateHours') {
      setDerInfoPostRateHours(value);
    } else if (name === 'derInfoPostRateMinutes') {
      setDerInfoPostRateMinutes(value);
    } else if (name === 'derInfoPostRateSeconds') {
      setDerInfoPostRateSeconds(value);
    } else if (name === 'meterDataPostRateHours') {
      setMeterDataPostRateHours(value);
    } else if (name === 'meterDataPostRateMinutes') {
      setMeterDataPostRateMinutes(value);
    } else if (name === 'meterDataPostRateSeconds') {
      setMeterDataPostRateSeconds(value);
    } else if (name === 'protocol') {
      setProtocol(value);
    }
  };

  const handleSearchInputChange = (selectedOptions, input) => {
    if (input === 'localTopologyNodeIdList') {
      const newTopoNodeValue = selectedOptions ? [selectedOptions.value] : [];
      setLocalTopologyNodeIdList(newTopoNodeValue);
    } else if (input === 'localIndependentNodeIdList') {
      const newIndeNodeValue = selectedOptions
        ? selectedOptions.map(option => option.value)
        : [];
      setLocalIndependentNodeIdList(newIndeNodeValue);
    } else if (input === 'aggregatorId') {
      const newAggregatorValue = selectedOptions ? selectedOptions.value : '';
      setAggregatorId(newAggregatorValue);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const parsedAsIntRegistrationPin = parseInt(registrationPin);
    const parsedAsIntDerInfoPostRateHours = parseInt(derInfoPostRateHours);
    const parsedAsIntDerInfoPostRateMinutes = parseInt(derInfoPostRateMinutes);
    const parsedAsIntDerInfoPostRateSeconds = parseInt(derInfoPostRateSeconds);
    const parsedAsIntMeterDataPostRateHours = parseInt(meterDataPostRateHours);
    const parsedAsIntMeterDataPostRateMinutes = parseInt(
      meterDataPostRateMinutes
    );
    const parsedAsIntMeterDataPostRateSeconds = parseInt(
      meterDataPostRateSeconds
    );

    const parsedAsIntProtocol = parseInt(protocol);

    let passed = true;
    if (
      registrationPin &&
      (!/^\d+$/.test(registrationPin) ||
        isNaN(parsedAsIntRegistrationPin) ||
        parsedAsIntRegistrationPin > 999999 ||
        parsedAsIntRegistrationPin < 0)
    ) {
      setRegistrationPinErrorOccurred(true);
      passed = false;
    } else {
      setRegistrationPinErrorOccurred(false);
    }

    const convertedDerInfoPostRate = convertToSeconds({
      hours: parsedAsIntDerInfoPostRateHours,
      minutes: parsedAsIntDerInfoPostRateMinutes,
      seconds: parsedAsIntDerInfoPostRateSeconds
    });

    const convertedMeterDataPostRate = convertToSeconds({
      hours: parsedAsIntMeterDataPostRateHours,
      minutes: parsedAsIntMeterDataPostRateMinutes,
      seconds: parsedAsIntMeterDataPostRateSeconds
    });

    if (
      Number.isInteger(parsedAsIntDerInfoPostRateHours) ||
      Number.isInteger(parsedAsIntDerInfoPostRateSeconds) ||
      Number.isInteger(parsedAsIntDerInfoPostRateMinutes)
    ) {
      if (
        isNaN(convertedDerInfoPostRate) ||
        convertedDerInfoPostRate > 4294967295 ||
        convertedDerInfoPostRate < 0
      ) {
        setDerInfoPostRateErrorOccurred(true);
        passed = false;
      } else {
        setDerInfoPostRateErrorOccurred(false);
      }
    }

    if (
      Number.isInteger(parsedAsIntMeterDataPostRateHours) ||
      Number.isInteger(parsedAsIntMeterDataPostRateSeconds) ||
      Number.isInteger(parsedAsIntMeterDataPostRateMinutes)
    ) {
      if (
        isNaN(convertedMeterDataPostRate) ||
        convertedMeterDataPostRate > 4294967295 ||
        convertedMeterDataPostRate < 0
      ) {
        setMeterDataPostRateErrorOccurred(true);
        passed = false;
      } else {
        setMeterDataPostRateErrorOccurred(false);
      }
    }

    const existingDeviceIds = devices.map(device => device.deviceId);
    const doesNewDeviceIdAlreadyExist = existingDeviceIds.some(
      existingDeviceId => existingDeviceId === deviceId
    );
    if (deviceId !== device.deviceId && doesNewDeviceIdAlreadyExist) {
      dispatch(
        openErrorModal(
          'Device id ' +
            deviceId +
            ' already exists. Please choose a different device Id'
        )
      );
      return;
    }

    if (!passed) {
      return;
    }

    const requestBody = {
      protocol: parsedAsIntProtocol,
      deviceId: deviceId
    };

    const fullIndeNodeIdList = remoteIndependentNodeIdList.concat(
      localIndependentNodeIdList
    );
    const fullTopologyNodeIdList = remoteTopologyNodeIdList.concat(
      localTopologyNodeIdList
    );

    if (externallyManagedEdevs) {
      if (device.localDevice === LOCAL_RESOURCE_MAPPED_VALUES.LOCAL) {
        requestBody.localDevice = LOCAL_RESOURCE_MAPPED_VALUES.LOCAL;
      } else {
        // If any of these conditions are true, the device should be remote plus local.
        // If none of them are true, the device should be remote.
        const isRemoteOrRemotePlusLocal =
          device.localDevice === LOCAL_RESOURCE_MAPPED_VALUES.REMOTE ||
          device.localDevice === LOCAL_RESOURCE_MAPPED_VALUES.REMOTE_PLUS_LOCAL;
        if (
          (aggregatorId && isRemoteOrRemotePlusLocal) ||
          (fullIndeNodeIdList.length &&
            isRemoteOrRemotePlusLocal &&
            localIndependentNodeIdList.length) ||
          (fullTopologyNodeIdList.length &&
            isRemoteOrRemotePlusLocal &&
            localTopologyNodeIdList.length) ||
          parsedAsIntProtocol !== PROTOCOL_OPTIONS['2030.5'].value
        ) {
          requestBody.localDevice =
            LOCAL_RESOURCE_MAPPED_VALUES.REMOTE_PLUS_LOCAL;
        } else {
          requestBody.localDevice = LOCAL_RESOURCE_MAPPED_VALUES.REMOTE;
        }
      }
    }

    if (aggregatorId !== '') {
      requestBody.aggregatorId = aggregatorId;
    }

    if (fullIndeNodeIdList.length) {
      requestBody.indeNodeIdList = fullIndeNodeIdList;
    }

    if (fullTopologyNodeIdList.length) {
      requestBody.topoNodeIdList = fullTopologyNodeIdList;
    }

    //Remote nodes have these 3 fields disabled
    if (!isNaN(parsedAsIntRegistrationPin)) {
      requestBody.registrationPin = parsedAsIntRegistrationPin;
    }
    if (Number.isInteger(convertedDerInfoPostRate)) {
      requestBody.edevPostRate = convertedDerInfoPostRate;
    }
    if (Number.isInteger(convertedMeterDataPostRate)) {
      requestBody.mupPostRate = convertedMeterDataPostRate;
    }

    // TODO: validate inputs
    try {
      await dispatch(editDevice(requestBody, device.deviceId));
      dispatch(revertSidePanel());
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const handleDelete = async () => {
    try {
      setShowDeleteModal(false);
      await dispatch(deleteDevice(id));
      dispatch(closeSidePanel());
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Compiles the list of node associations a user can make for a resource. It will exclude currently selected choices
   * and remote nodes if necessary.
   * @param {Array} currentList - current list of selected nodes
   * @param {Array} fullResourceList - full list of the node resources. Array of objects
   * @returns {Array} - list of options for user to select to associate
   */
  const getParentAssociationOptionIds = (
    currentList = [],
    fullResourceList = []
  ) => {
    // Due to internal implementation of react-select package, currentList sometimes returns as an empty string.
    const parentAssocationListToParse = Array.isArray(currentList)
      ? currentList
      : [];
    const parentAssociationOptionIds = [];
    if (fullResourceList.length) {
      fullResourceList.forEach(resourceItem => {
        const resourceItemId =
          resourceItem.topoNodeId || resourceItem.indeNodeId;
        const isPresentInCurrentList = parentAssocationListToParse.find(
          parentAssociationItem => resourceItemId === parentAssociationItem
        );
        if (
          !isPresentInCurrentList &&
          (!externallyManagedEdevs ||
            (externallyManagedEdevs &&
              resourceItem.localNode === LOCAL_RESOURCE_MAPPED_VALUES.LOCAL))
        ) {
          parentAssociationOptionIds.push(resourceItemId);
        }
      });
    }
    return parentAssociationOptionIds;
  };

  const getLocalNodesFromList = (nodeList = []) => {
    return nodeList.filter(node => {
      const topoNode =
        topoNodes.find(topoNode => topoNode.topoNodeId === node) || {};
      return topoNode.localNode === LOCAL_RESOURCE_MAPPED_VALUES.LOCAL;
    });
  };

  if (!isActive || (isActive && isLoading) || device === undefined) {
    return null;
  } else {
    const aggregatorIds = aggregators.map(
      aggregator => aggregator.aggregatorId
    );
    const isRemoteResource =
      externallyManagedEdevs && isExternallyManaged(device.localDevice, true);

    return (
      <>
        <StyledForm onSubmit={handleSubmit}>
          <FullPanel>
            <Panel title="Selected Device Info » Edit" primaryHeader />
            <PanelContent>
              <SubPanel>
                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">ID</Text>
                  </PanelRowDescriptor>
                  <PanelRowTextForm
                    name="deviceId"
                    onChange={handleInputChange}
                    value={deviceId}
                    type="text"
                    min="0"
                    max="64"
                  />
                </SubPanelRow>
                {externallyManagedEdevs && (
                  <SubPanelRow>
                    <PanelRowDescriptor>
                      <Text fontSize="12px">Remotely Managed</Text>
                    </PanelRowDescriptor>
                    <PanelRowDisplay>
                      <Text fontSize="12px">
                        {isRemoteResource ? 'True' : 'False'}
                      </Text>
                    </PanelRowDisplay>
                  </SubPanelRow>
                )}
                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">Registration PIN</Text>
                    <Text fontSize="12px">(Optional)</Text>
                  </PanelRowDescriptor>
                  <PanelRowDisplay>
                    <ShowTooltipOnChildren
                      message="Cannot modify remote resource"
                      showTooltip={isRemoteResource}
                    >
                      <PanelRowTextForm
                        name="registrationPin"
                        onChange={handleInputChange}
                        value={registrationPin}
                        type="number"
                        min="0"
                        max="999999"
                        disabled={isRemoteResource}
                      />
                    </ShowTooltipOnChildren>
                  </PanelRowDisplay>
                </SubPanelRow>

                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">Requested DER Info Post Rate</Text>
                    <Text fontSize="12px">(Optional)</Text>
                  </PanelRowDescriptor>

                  <ShowTooltipOnChildren
                    message="Cannot modify remote resource"
                    showTooltip={isRemoteResource}
                  >
                    {/* hours */}
                    <MultipleInputRowContainer>
                      <InputAndLabelContainer width="30%">
                        <LabledInput
                          type="number"
                          name="derInfoPostRateHours"
                          value={derInfoPostRateHours}
                          onChange={handleInputChange}
                          min="0"
                          max="11929"
                          isError={derInfoPostRateErrorOccurred}
                          disabled={isRemoteResource}
                        />
                        <InputUnitLabel>hrs</InputUnitLabel>
                      </InputAndLabelContainer>
                      {/* minutes */}
                      <InputAndLabelContainer width="30%">
                        <LabledInput
                          type="number"
                          name="derInfoPostRateMinutes"
                          value={derInfoPostRateMinutes}
                          onChange={handleInputChange}
                          min="0"
                          max="60"
                          isError={derInfoPostRateErrorOccurred}
                          disabled={isRemoteResource}
                        />
                        <InputUnitLabel>min</InputUnitLabel>
                      </InputAndLabelContainer>
                      {/* seconds */}
                      <InputAndLabelContainer width="30%">
                        <LabledInput
                          type="number"
                          name="derInfoPostRateSeconds"
                          value={derInfoPostRateSeconds}
                          onChange={handleInputChange}
                          min="0"
                          max="60"
                          isError={derInfoPostRateErrorOccurred}
                          disabled={isRemoteResource}
                        />
                        <InputUnitLabel>sec</InputUnitLabel>
                      </InputAndLabelContainer>
                    </MultipleInputRowContainer>
                  </ShowTooltipOnChildren>
                </SubPanelRow>

                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">Requested Meter Data Post Rate</Text>
                    <Text fontSize="12px">(Optional)</Text>
                  </PanelRowDescriptor>

                  <ShowTooltipOnChildren
                    message="Cannot modify remote resource"
                    showTooltip={isRemoteResource}
                  >
                    {/* hours */}
                    <MultipleInputRowContainer>
                      <InputAndLabelContainer width="30%">
                        <LabledInput
                          type="number"
                          name="meterDataPostRateHours"
                          value={meterDataPostRateErrorOccurred}
                          onChange={handleInputChange}
                          min="0"
                          max="11929"
                          isError={meterDataPostRateErrorOccurred}
                          disabled={isRemoteResource}
                        />
                        <InputUnitLabel>hrs</InputUnitLabel>
                      </InputAndLabelContainer>
                      {/* minutes */}
                      <InputAndLabelContainer width="30%">
                        <LabledInput
                          type="number"
                          name="meterDataPostRateMinutes"
                          value={meterDataPostRateMinutes}
                          onChange={handleInputChange}
                          min="0"
                          max="60"
                          isError={meterDataPostRateErrorOccurred}
                          disabled={isRemoteResource}
                        />
                        <InputUnitLabel>min</InputUnitLabel>
                      </InputAndLabelContainer>
                      {/* seconds */}
                      <InputAndLabelContainer width="30%">
                        <LabledInput
                          type="number"
                          name="meterDataPostRateSeconds"
                          value={meterDataPostRateSeconds}
                          onChange={handleInputChange}
                          min="0"
                          max="60"
                          isError={meterDataPostRateErrorOccurred}
                          disabled={isRemoteResource}
                        />
                        <InputUnitLabel>sec</InputUnitLabel>
                      </InputAndLabelContainer>
                    </MultipleInputRowContainer>
                  </ShowTooltipOnChildren>
                </SubPanelRow>

                {deviceConfigFlag && (
                  <SubPanelRow>
                    <PanelRowDescriptor>
                      <Text fontSize="12px">Protocol</Text>
                      <Text fontSize="12px">(Optional)</Text>
                    </PanelRowDescriptor>
                    <RadioButtons>
                      <RadioOption>
                        <label>
                          <RadioInput
                            name="protocol"
                            onChange={handleInputChange}
                            type="radio"
                            value={PROTOCOL_OPTIONS['2030.5'].value}
                            checked={
                              parseInt(protocol) ===
                              PROTOCOL_OPTIONS['2030.5'].value
                            }
                          />
                          {PROTOCOL_OPTIONS['2030.5'].title}
                        </label>
                      </RadioOption>
                      <RadioOption>
                        <label>
                          <RadioInput
                            name="protocol"
                            onChange={handleInputChange}
                            type="radio"
                            value={PROTOCOL_OPTIONS.Crosslink.value}
                            checked={
                              parseInt(protocol) ===
                              PROTOCOL_OPTIONS.Crosslink.value
                            }
                          />
                          {PROTOCOL_OPTIONS.Crosslink.title}
                        </label>
                      </RadioOption>
                    </RadioButtons>
                  </SubPanelRow>
                )}
              </SubPanel>

              <Break />

              <SubPanel>
                <SubPanelRow>
                  <Text fontSize="12px" fontWeight="600">
                    Edit Associations (Optional)
                  </Text>
                </SubPanelRow>
                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">
                      {externallyManagedEdevs && 'Local'} Parent Topology Node
                    </Text>
                  </PanelRowDescriptor>
                  <PanelRowDisplay>
                    <SelectableSearchInput
                      options={getParentAssociationOptionIds(
                        localTopologyNodeIdList,
                        topoNodes
                      )}
                      onChange={selected =>
                        handleSearchInputChange(
                          selected,
                          'localTopologyNodeIdList'
                        )
                      }
                      selected={localTopologyNodeIdList}
                      name="toponode-select"
                    />
                  </PanelRowDisplay>
                </SubPanelRow>
                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">
                      {externallyManagedEdevs && 'Local'} Independent Nodes
                    </Text>
                  </PanelRowDescriptor>
                  <PanelRowDisplay>
                    <SelectableSearchInput
                      isMulti={true}
                      options={getParentAssociationOptionIds(
                        localIndependentNodeIdList,
                        indeNodes
                      )}
                      onChange={selected =>
                        handleSearchInputChange(
                          selected,
                          'localIndependentNodeIdList'
                        )
                      }
                      selected={localIndependentNodeIdList}
                      name="indenode-select"
                    />
                  </PanelRowDisplay>
                </SubPanelRow>
                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">Aggregator ID</Text>
                  </PanelRowDescriptor>
                  <PanelRowDisplay>
                    <SelectableSearchInput
                      isMulti={false}
                      options={aggregatorIds}
                      onChange={selected =>
                        handleSearchInputChange(selected, 'aggregatorId')
                      }
                      selected={aggregatorId}
                      name="aggregator-select"
                    />
                  </PanelRowDisplay>
                </SubPanelRow>
              </SubPanel>
              <Break />

              <SubPanel>
                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">Delete Device</Text>
                  </PanelRowDescriptor>
                  <ShowTooltipOnChildren
                    message="Cannot delete remote resource"
                    showTooltip={isRemoteResource}
                  >
                    <BlueBorderButton
                      type="button"
                      onClick={() => toggleDeleteModal()}
                      disabled={isRemoteResource}
                    >
                      Delete Device
                    </BlueBorderButton>
                  </ShowTooltipOnChildren>
                </SubPanelRow>
              </SubPanel>
            </PanelContent>
            <PanelButtonRow>
              <BlueBorderButton
                type="button"
                onClick={() => dispatch(revertSidePanel())}
              >
                Cancel
              </BlueBorderButton>
              <BlueButton type="submit" disabled={isSubmitting}>
                Save Changes
              </BlueButton>
            </PanelButtonRow>
          </FullPanel>
        </StyledForm>
        {showDeleteModal ? (
          <DeleteModal
            toggleDeleteModal={toggleDeleteModal}
            handleDelete={handleDelete}
          />
        ) : null}
      </>
    );
  }
};

export default EditDevice;
